import { config } from "@/config";
import { pb } from "@/lib/pocketbase";
import { localTranscribeSupported } from "./local-transcribe.support";

/**
 * KI-Client (Groq, OpenAI-kompatible Schnittstelle).
 *
 * Der Schlüssel liegt NICHT mehr im Frontend/PocketBase-Settings, sondern
 * server-seitig im PocketBase-Hook (pocketbase/pb_hooks/hooks.pb.js, Route
 * `POST /api/ai/chat`, Env-Variable GROQ_API_KEY). Das Frontend schickt dort
 * nur Tier + Nachrichten hin – die eingeloggte Sitzung reicht, kein eigener
 * Schlüssel nötig. `config.ai.apiKey` bleibt als Direktmodus für lokales
 * Entwickeln (umgeht PocketBase und ruft Groq selbst auf).
 *
 * tier="small"  → schnelle einfache Aufgaben (Quiz, Karteikarten)
 * tier="medium" → Klausur, Benotung, anspruchsvolle Generierung
 */

export type AiTier = "small" | "medium";

export function aiAvailableSync(): boolean {
  return Boolean(config.ai.apiKey) || pb.authStore.isValid;
}

export async function aiAvailable(): Promise<boolean> {
  return aiAvailableSync();
}

export interface ChatMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

/** Antwortform der OpenAI-kompatiblen Schnittstelle (inkl. Reasoning-Feldern). */
interface ChatResponse {
  choices?: { message?: { content?: string; reasoning?: string; reasoning_content?: string } }[];
}

/**
 * Den eigentlichen Text aus der Antwort holen.
 *
 * „Denkende" Modelle (z. B. nemotron-lightning) legen ihre Ausgabe je nach
 * Anbieter in `reasoning`/`reasoning_content` statt in `content` ab und lassen
 * `content` leer. Wer nur `content` liest, bekommt dann einen leeren String
 * und meldet fälschlich „keine Antwort" – genau das war der Fehler bei
 * Sparkles und Gespräch.
 */
function messageText(data: ChatResponse): string {
  const m = data.choices?.[0]?.message;
  return (m?.content || m?.reasoning || m?.reasoning_content || "").trim();
}

/** Direkter Anbieter (Dev-Modus oder Ausweichanbieter) – spricht Groq/OpenAI direkt an. */
interface DirectProvider {
  kind: "direct";
  baseUrl: string;
  apiKey: string;
  model: string;
}
/** Server-Proxy: kein Schlüssel im Client, nur Tier – siehe hooks.pb.js. */
interface ProxyProvider {
  kind: "proxy";
  tier: AiTier;
}
type Provider = DirectProvider | ProxyProvider;

/**
 * Zeit, nach der ein Anbieter als „antwortet nicht" gilt. Muss ÜBER der
 * Zeitgrenze im PocketBase-Hook (70 s, siehe hooks.pb.js) liegen, sonst
 * bricht der Client ab, bevor der Server seinen sauberen Fehler schicken
 * kann. Große Generierungen (Lernpfad, Lektions-Aufgaben: 5000+ Tokens bei
 * viel Material im Prompt) brauchten öfter länger als die alten 55/75 s –
 * beide Seiten angehoben.
 */
const TIMEOUT_MS = 90_000;

/** Wie oft bei Überlastung (429/5xx/Abbruch) beim selben Anbieter nachgefasst wird. */
const RETRIES = 2;

/**
 * Groqs kostenloses On-Demand-Kontingent lehnt (Stand August 2026, Modell
 * `openai/gpt-oss-120b`) jede EINZELNE Anfrage mit Status 413 ab, deren Prompt
 * + angefordertes `max_tokens` zusammen ein Minuten-Kontingent von 8000 Token
 * übersteigt – das ist keine Drosselung über die Zeit (dafür gäbe es 429),
 * sondern eine harte Grenze pro Anfrage. Da der Server standardmäßig
 * ausschließlich über Groq läuft (`AI_CHAT_GROQ_ONLY`, siehe hooks.pb.js),
 * müssen Aufrufer mit viel Kontextmaterial (Lernpfad, Lektions-Aufgaben,
 * Sparkles) selbst in mehreren kleinen Anfragen arbeiten statt einer großen.
 * Dieser Wert ist der Rahmen dafür, mit Sicherheitsabstand nach unten.
 */
export const GROQ_SAFE_PROMPT_TOKENS = 7000;

/**
 * Grobe, bewusst konservative Schätzung (3 statt der oft genannten 4 Zeichen
 * pro Token): deutsche Wörter und JSON-lastige Prompts sind dichter, und
 * Groqs 413-Grenze kennt keinen eigenen Sicherheitsabstand.
 */
export function estimateTokens(text: string): number {
  return Math.ceil(text.length / 3);
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function providersFor(tier: AiTier): Provider[] {
  const list: Provider[] = [
    config.ai.apiKey
      ? {
          kind: "direct",
          baseUrl: config.ai.baseUrl,
          apiKey: config.ai.apiKey,
          model: tier === "medium" ? config.ai.modelMedium : config.ai.modelSmall,
        }
      : { kind: "proxy", tier },
  ];
  for (const extra of config.ai.fallbacks) {
    if (!extra.apiKey || !extra.baseUrl) continue;
    list.push({
      kind: "direct",
      baseUrl: extra.baseUrl,
      apiKey: extra.apiKey,
      model: tier === "medium" ? extra.modelMedium : extra.modelSmall,
    });
  }
  return list;
}

interface AskOpts {
  maxTokens?: number;
  temperature?: number;
  /** Erzwingt (best-effort) reines JSON als Antwort – siehe aiJson/aiJsonArray. */
  jsonMode?: boolean;
}

/** Ein Versuch bei einem Anbieter. Wirft mit sprechender Meldung. */
async function askOnce(provider: Provider, messages: ChatMessage[], opts: AskOpts): Promise<string> {
  if (provider.kind === "proxy") return askProxy(provider.tier, messages, opts);

  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`${provider.baseUrl.replace(/\/$/, "")}/chat/completions`, {
      method: "POST",
      signal: abort.signal,
      headers: {
        Authorization: `Bearer ${provider.apiKey}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: provider.model,
        messages,
        max_tokens: opts.maxTokens ?? 2048,
        temperature: opts.temperature ?? 0.4,
        ...(opts.jsonMode ? { response_format: { type: "json_object" } } : {}),
      }),
    });

    if (!res.ok) {
      const body = await res.text().catch(() => "");
      const error = new Error(readableAiError(res.status, body));
      (error as Error & { status?: number }).status = res.status;
      throw error;
    }
    return messageText((await res.json()) as ChatResponse);
  } finally {
    clearTimeout(timer);
  }
}

/** Anfrage über den PocketBase-Hook statt direkt an Groq – kein Client-Schlüssel nötig. */
async function askProxy(tier: AiTier, messages: ChatMessage[], opts: AskOpts): Promise<string> {
  // Eigene Zeitgrenze, sonst hängt die Anfrage im Zweifel, bis irgendein
  // Zwischenserver die Verbindung kappt – und das sieht dann wie „Server tot" aus.
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), TIMEOUT_MS);
  try {
    const data = await pb.send<ChatResponse>("/api/ai/chat", {
      method: "POST",
      body: {
        tier,
        messages,
        maxTokens: opts.maxTokens ?? 2048,
        temperature: opts.temperature ?? 0.4,
        jsonMode: opts.jsonMode ?? false,
      },
      signal: abort.signal,
      // Sonst bricht die SDK eine bereits laufende Anfrage an denselben Pfad ab,
      // sobald ein zweiter KI-Aufruf startet (z. B. parallele Aufgaben-Generierung).
      requestKey: null,
    });
    return messageText(data);
  } catch (err) {
    throw await proxyError(err);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Aus einem gescheiterten Proxy-Aufruf einen Fehler machen, der sagt, was
 * wirklich los ist.
 *
 * Status 0 bedeutet nur „fetch hat aufgegeben" – das kann Zeitüberschreitung,
 * Funkloch ODER ein toter Server sein. Vorher wurde daraus pauschal „Ist
 * PocketBase erreichbar?", obwohl PocketBase meistens lief und in Wahrheit
 * die KI-Antwort zu lange dauerte. Deshalb wird jetzt einmal kurz gegen
 * /api/health geprüft und danach die richtige Ursache benannt.
 */
async function proxyError(err: unknown): Promise<Error> {
  const status = (err as { status?: number })?.status ?? 0;
  const upstream =
    (err as { response?: { message?: string } })?.response?.message ||
    (err as { data?: { message?: string } })?.data?.message ||
    "";
  // Roh in die Browser-Konsole – die Fehlermeldung im UI ist bewusst kurz
  // gehalten, hier steht der ganze Rest fürs Debuggen (Status, Server-Text,
  // Original-Objekt), ohne dass man erst das Netzwerk-Tab durchsuchen muss.
  console.error("[ai] Anfrage fehlgeschlagen", { status, upstream, err });

  let text: string;
  if (status === 0) {
    text = (await serverReachable())
      ? "Die KI hat nicht rechtzeitig geantwortet (Zeitüberschreitung). Der Server läuft – " +
        "bitte noch einmal versuchen, ggf. mit weniger Aufgaben auf einmal."
      : `Keine Verbindung zum Server (${config.pocketbaseUrl}). Internet prüfen oder URL in src/config.ts.`;
  } else if (status === 401) {
    text = "Bitte neu einloggen – die KI-Anfrage wurde nicht autorisiert.";
  } else if (status === 404) {
    text =
      "Der KI-Proxy fehlt auf dem Server. pocketbase/pb_hooks/hooks.pb.js nach pb_hooks/ " +
      "kopieren und PocketBase neu starten.";
  } else if (status === 503 || status === 502) {
    text = upstream || "Der KI-Dienst hat gerade eine Störung.";
  } else if (status === 429) {
    text = "Die KI ist gerade ausgelastet oder das Kontingent ist erschöpft.";
  } else {
    text = upstream || (err as Error)?.message || "KI-Anfrage fehlgeschlagen.";
  }

  const error = new Error(text) as Error & { status?: number; aiHandled?: boolean };
  error.status = status;
  // Markierung für pbErrorMessage: der Text steht schon fertig da und darf
  // nicht noch einmal in „Ist PocketBase erreichbar?" umgeschrieben werden.
  error.aiHandled = true;
  return error;
}

/**
 * Kurzer Lebenszeichen-Test des Backends, nur für die Fehlermeldung.
 *
 * 2,5 s waren zu knapp: im LAN (siehe CLAUDE.md, Dev-Server ist absichtlich im
 * LAN erreichbar) oder wenn PocketBase gerade eine lange KI-Anfrage bedient,
 * kann /api/health auch mal etwas länger brauchen, obwohl der Server läuft.
 * Das führte dazu, dass die Diagnose fälschlich "nicht erreichbar" meldete,
 * obwohl man die Datenbank im selben Moment im Admin-UI sehen konnte.
 */
async function serverReachable(): Promise<boolean> {
  // Über den SDK-Client statt eigenem `fetch()`: ein roher Cross-Origin-Fetch
  // auf eine andere Domain (PocketBase Cloud) kann an CORS scheitern, obwohl
  // der Server bestens erreichbar ist – die SDK-Anfragen (die sonst überall
  // funktionieren) laufen über denselben Client, der das schon korrekt
  // handhabt. Ohne diesen Umweg meldete die Diagnose fast immer fälschlich
  // "keine Verbindung", selbst wenn nur die KI-Anfrage selbst scheiterte.
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), 8000);
  try {
    await pb.health.check({ signal: abort.signal });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/* --------------------------- Spracherkennung --------------------------- */

/**
 * Audio zu Text (Whisper large v3 turbo über den PocketBase-Hook).
 * `hint` sind Fachbegriffe aus den Notizen – Whisper schreibt sie damit
 * deutlich zuverlässiger richtig.
 */
export async function transcribe(audio: Blob, hint = ""): Promise<string> {
  // Zuerst lokal versuchen (Whisper im Browser, siehe
  // local-transcribe.service.ts): kein Upload, keine Wartezeit auf einen
  // fremden Dienst, funktioniert auch ohne Netz. Nur wenn das nicht geht
  // (kein Worker/WebAudio, Modell nicht ladbar), übernimmt der Server.
  if (localTranscribeSupported()) {
    try {
      const { transcribeLocal } = await import("./local-transcribe.service");
      const local = await transcribeLocal(audio);
      if (local.trim()) return local;
    } catch {
      /* weiter unten über den Server versuchen */
    }
  }

  const fd = new FormData();
  const ext = audio.type.includes("mp4") ? "mp4" : audio.type.includes("ogg") ? "ogg" : "webm";
  fd.append("file", audio, `aufnahme.${ext}`);
  if (hint) fd.append("prompt", hint.slice(0, 800));

  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), TIMEOUT_MS);
  try {
    const data = await pb.send<{ text?: string }>("/api/ai/transcribe", {
      method: "POST",
      body: fd,
      signal: abort.signal,
      requestKey: null,
    });
    return (data.text ?? "").trim();
  } catch (err) {
    throw await proxyError(err);
  } finally {
    clearTimeout(timer);
  }
}

/* ------------------------------ Sprachausgabe ------------------------------ */

function base64ToBlob(base64: string, mime: string): Blob {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/** Text zu Sprache (OpenRouter TTS über den PocketBase-Hook) – gibt eine abspielbare Audiodatei zurück. */
export async function speak(text: string, voice?: string): Promise<Blob> {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), TIMEOUT_MS);
  try {
    const data = await pb.send<{ audioBase64: string; mime: string }>("/api/ai/speak", {
      method: "POST",
      body: { text, voice },
      signal: abort.signal,
      requestKey: null,
    });
    return base64ToBlob(data.audioBase64, data.mime || "audio/mpeg");
  } catch (err) {
    throw await proxyError(err);
  } finally {
    clearTimeout(timer);
  }
}

/* ------------------------------ Diagnose ------------------------------ */

export interface AiStatus {
  keySet: boolean;
  ok: boolean;
  message: string;
}

/**
 * Selbstdiagnose für den Fehlerfall: sagt, ob der Server läuft, ob der
 * KI-Proxy installiert ist und ob Groq den Schlüssel annimmt.
 */
export async function aiStatus(): Promise<AiStatus> {
  if (!(await serverReachable())) {
    return { keySet: false, ok: false, message: `Server nicht erreichbar: ${config.pocketbaseUrl}` };
  }
  if (!pb.authStore.isValid) {
    return { keySet: false, ok: false, message: "Nicht eingeloggt – die KI braucht eine Sitzung." };
  }
  try {
    return await pb.send<AiStatus>("/api/ai/status", { method: "GET", requestKey: null });
  } catch (err) {
    const status = (err as { status?: number })?.status ?? 0;
    if (status === 404) {
      return {
        keySet: false,
        ok: false,
        message:
          "KI-Proxy fehlt: pocketbase/pb_hooks/hooks.pb.js auf den Server nach pb_hooks/ " +
          "kopieren und PocketBase neu starten.",
      };
    }
    return { keySet: false, ok: false, message: (err as Error)?.message ?? "Unbekannter Fehler." };
  }
}

/** Aus Statuscode und Antwortkörper eine Meldung machen, die weiterhilft. */
function readableAiError(status: number, body: string): string {
  if (status === 401 || status === 403) return "Der KI-Schlüssel wird nicht akzeptiert.";
  if (status === 429) return "Die KI ist gerade ausgelastet oder das Kontingent ist erschöpft.";
  if (status === 402) return "Das Guthaben des KI-Zugangs ist aufgebraucht.";
  if (status >= 500) return "Der KI-Dienst hat gerade eine Störung.";
  return `KI-Anfrage fehlgeschlagen (${status}). ${body.slice(0, 200)}`;
}

/**
 * Anfrage an die KI – mit Zeitgrenze, einem Nachfassen bei Überlastung und
 * Ausweichanbietern (siehe config.ai.fallbacks). Erst wenn keiner kann,
 * kommt ein Fehler heraus; er nennt dann den Grund des letzten Versuchs.
 */
export async function aiChat(
  messages: ChatMessage[],
  opts: { maxTokens?: number; temperature?: number; tier?: AiTier; jsonMode?: boolean } = {}
): Promise<string> {
  const providers = providersFor(opts.tier ?? "small");
  let last: Error = new Error("Keine KI erreichbar.");

  for (const provider of providers) {
    for (let attempt = 0; attempt <= RETRIES; attempt++) {
      try {
        return await askOnce(provider, messages, opts);
      } catch (err) {
        last = err instanceof Error ? err : new Error(String(err));
        const status = (last as Error & { status?: number }).status ?? 0;
        // Schlüssel falsch, Proxy fehlt oder Anfrage fehlerhaft: nachfassen
        // bringt nichts, ein anderer Anbieter vielleicht schon. 503 kommt vom
        // eigenen Hook, wenn GROQ_API_KEY fehlt – das heilt kein Neuversuch.
        const worthRetrying =
          status !== 503 && status !== 404 && (status === 429 || status >= 500 || status === 0);
        if (!worthRetrying || attempt === RETRIES) break;
        await sleep(1200 * (attempt + 1));
      }
    }
  }
  throw last;
}

/** Chat mit JSON-Antwort: schält ```json-Fences ab und parst (mit Reparatur). */
export async function aiJson<T>(
  system: string,
  user: string,
  maxTokens = 3000,
  tier: AiTier = "small"
): Promise<T> {
  const raw = await aiChat(
    [
      { role: "system", content: system + "\nAntworte AUSSCHLIESSLICH mit gültigem JSON, ohne Erklärtext." },
      { role: "user", content: user },
    ],
    { maxTokens, temperature: 0.5, tier, jsonMode: true }
  );
  try {
    return parseJsonLoose<T>(raw);
  } catch (err) {
    // Anders als ein HTTP-Fehlschlag (siehe proxyError) kommt hier eine
    // ganz normale 200er-Antwort zurück, die sich nur nicht als JSON parsen
    // lässt – bisher landete das NIRGENDS in der Konsole, man sah nur die
    // knappe UI-Meldung ohne zu wissen, was das Modell wirklich geantwortet
    // hat. Der Rohtext hier zeigt sofort, ob z. B. bei max_tokens abgeschnitten
    // wurde oder das Modell Erklärtext drumherum gesetzt hat.
    console.error("[ai] Antwort ist kein gültiges JSON", { raw, err });
    throw err;
  }
}

/**
 * Wie `aiJson`, aber der Aufrufer gibt die Nachrichten selbst vor (z. B. mit
 * mehreren Quellenblöcken) und bekommt ein Array zurück. Fehlende oder
 * abgeschnittene Elemente werden verworfen statt den ganzen Lauf zu killen.
 */
export async function aiJsonArray<T>(
  messages: ChatMessage[],
  opts: { maxTokens?: number; temperature?: number; tier?: AiTier } = {}
): Promise<T[]> {
  const raw = await aiChat(messages, { temperature: 0.5, ...opts });
  const parsed = parseJsonLoose<T[] | { items?: T[] }>(raw);
  if (Array.isArray(parsed)) return parsed;
  const items = (parsed as { items?: T[] })?.items;
  return Array.isArray(items) ? items : [];
}

/**
 * Robustes JSON-Parsing für KI-Antworten. KI-Modelle liefern oft ungültige
 * Backslash-Escapes (z. B. LaTeX \frac, \( oder einzelne \) in Strings, die
 * JSON.parse mit "Bad escaped character" abbrechen lassen.
 */
/**
 * Alle Klammer-Blöcke eines Textes, von außen nach innen und vom längsten
 * zum kürzesten. Nötig für Modelle, die vor der eigentlichen Antwort laut
 * nachdenken ("Ich sollte {…} zurückgeben, also:") – dort ist der ERSTE
 * Block im Text nicht die Antwort, sondern Teil der Überlegung. Vorher schnitt
 * der Parser stur ab der ersten Klammer und scheiterte an genau diesen
 * Antworten (Symptom: „Die KI hat keinen Vorschlag geliefert").
 */
function jsonCandidates(text: string): string[] {
  const out: { s: string; len: number }[] = [];
  for (const open of ["[", "{"] as const) {
    const close = open === "[" ? "]" : "}";
    for (let i = 0; i < text.length; i++) {
      if (text[i] !== open) continue;
      let depth = 0;
      let inStr = false;
      let esc = false;
      for (let j = i; j < text.length; j++) {
        const c = text[j];
        if (inStr) {
          if (esc) esc = false;
          else if (c === "\\") esc = true;
          else if (c === '"') inStr = false;
          continue;
        }
        if (c === '"') inStr = true;
        else if (c === open) depth++;
        else if (c === close) {
          depth--;
          if (depth === 0) {
            out.push({ s: text.slice(i, j + 1), len: j + 1 - i });
            break;
          }
        }
      }
    }
  }
  return out.sort((a, b) => b.len - a.len).map((o) => o.s);
}

export function parseJsonLoose<T>(raw: string): T {
  const stripped = raw.replace(/```json?/gi, "").replace(/```/g, "");
  // Erst der klassische Weg (ab erster Klammer), dann die vollständigen
  // Blöcke nach Länge – der längste ist bei „denkenden" Modellen die Antwort,
  // nicht die beiläufig erwähnte Struktur im Erklärtext.
  const head = stripped.replace(/^[\s\S]*?([[{])/, "$1").trim();

  const candidates = [head, ...jsonCandidates(stripped)];
  for (const cand of candidates) {
    if (!cand) continue;
    for (const variant of [cand, fixEscapes(cand)]) {
      try { return JSON.parse(variant) as T; } catch { /* next */ }
    }
  }
  // Zuletzt: abgeschnittene Antworten (max_tokens erreicht) reparieren.
  for (const cand of [truncateToValid(head), fixEscapes(truncateToValid(head))]) {
    if (!cand) continue;
    try { return JSON.parse(cand) as T; } catch { /* next */ }
  }
  throw new Error("KI-Antwort ist kein gültiges JSON (vermutlich abgeschnitten).");
}

function fixEscapes(s: string): string {
  return s.replace(/\\(?!["\\/bfnrtu])/g, "\\\\");
}

function truncateToValid(s: string): string {
  const closes: number[] = [];
  let inStr = false, esc = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inStr) {
      if (esc) esc = false;
      else if (c === "\\") esc = true;
      else if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') inStr = true;
    else if (c === "}" || c === "]") closes.push(i);
  }
  for (let k = closes.length - 1; k >= 0 && k > closes.length - 60; k--) {
    const cand = close(s.slice(0, closes[k] + 1));
    try { JSON.parse(cand); return cand; } catch { /* weiter */ }
  }
  return "";
}

function close(head: string): string {
  const open: string[] = [];
  let q = false, e = false;
  for (const c of head) {
    if (q) { if (e) e = false; else if (c === "\\") e = true; else if (c === '"') q = false; continue; }
    if (c === '"') q = true;
    else if (c === "{" || c === "[") open.push(c);
    else if (c === "}" || c === "]") open.pop();
  }
  return head.replace(/,\s*$/, "") + open.reverse().map((c) => (c === "{" ? "}" : "]")).join("");
}
